// カーソルキー操作をシミュレートする
// contenteditableな要素を想定
// TODO SHIFT+でレンジ選択ができてない
export class ArrowKeySimulate {
  // 左カーソルキー押下で要素を左へ移動
  Left (editableElement: HTMLElement) {
    ArrowKeySimulate.do(editableElement, (sel, _, line) => {
      let targetNode = sel.anchorNode;
      let targetIndex = sel.anchorOffset - 1;

      // 行要素そのものだったらその子供を対象にする
      if (targetNode === line) {
        targetNode = line.firstChild;
      }
      if (targetNode == null) {
        return;
      }
      // ゼロだったら親要素たどってtargetNodeを書き換える
      if (targetIndex < 0) {
        const prevNode = ArrowKeySimulate.findPrevTextNode(
          editableElement,
          targetNode
        );
        if (prevNode == null) {
          targetIndex = 0;
        } else {
          targetNode = prevNode;
          targetIndex =
            prevNode.nodeName == "#text"
              ? (prevNode.textContent?.length ?? 1) - 1
              : 0;
        }
      }
      return {
        targetNode: targetNode,
        targetIndex: targetIndex,
      };
    });
  }
  // 右カーソルキー押下で要素を右へ移動
  Right (editableElement: HTMLElement) {
    ArrowKeySimulate.do(editableElement, (sel, lines, line) => {
      let targetNode = sel.anchorNode;
      let targetIndex = sel.anchorOffset + 1;
      // 行要素そのものだったらその子供を対象にする
      if (targetNode === line) {
        targetNode = line.firstChild;
      }
      if (targetNode == null) {
        return;
      }
      // 文字数以上だったら親要素たどってtargetNodeを書き換える
      if ((targetNode.textContent?.length ?? 0) < targetIndex) {
        const nextNode = ArrowKeySimulate.findNextTextNode(
          editableElement,
          targetNode
        );
        if (nextNode == null) {
          targetIndex = 0;
        } else {
          targetNode = nextNode;
          targetIndex = nextNode.nodeName == "#text" ? 1 : 0;
        }
      }
      return {
        targetNode: targetNode,
        targetIndex: targetIndex,
      };
    });
  }
  // 上カーソルキー押下で要素を上へ移動
  Up (editableElement: HTMLElement) {
    ArrowKeySimulate.do(editableElement, (sel, lines, line) => {
      // 右の行を探す
      const currentIndex = lines.indexOf(line);
      const nextIndex = currentIndex - 1;
      if (nextIndex < 0) {
        return undefined;
      }
      if (!sel.anchorNode) return undefined;
      const nextLine = lines[nextIndex];
      return ArrowKeySimulate.moveLine(
        line,
        sel.anchorNode,
        sel.anchorOffset,
        nextLine
      );
    });
  }
  // 下カーソルキー押下で要素を下へ移動
  Down (editableElement: HTMLElement) {
    ArrowKeySimulate.do(editableElement, (sel, lines, line) => {
      // 右の行を探す
      const currentIndex = lines.indexOf(line);
      const nextIndex = currentIndex + 1;
      if (lines.length <= nextIndex) {
        return undefined;
      }
      if (!sel.anchorNode) return undefined;
      const nextLine = lines[nextIndex];
      return ArrowKeySimulate.moveLine(
        line,
        sel.anchorNode,
        sel.anchorOffset,
        nextLine
      );
    });
  }

  // キー操作の実行
  static do (
    editableElement: HTMLElement,
    f: (
      sel: Selection,
      lines: Node[],
      currentLine: Node
    ) => { targetNode: Node; targetIndex: number } | undefined
  ) {
    // 現在のカーソル位置を取得
    const sel = window.getSelection();
    if (sel == null) {
      return;
    }
    // まずは行単位で要素を取得
    const lines = Array.from(editableElement.childNodes).map((x) => x as Node);
    // 現在の行とカーソル位置(文字数)を取得
    const line = ArrowKeySimulate.getLineFromInnerNode(
      lines,
      sel.anchorNode as Node
    );
    if (line === undefined) {
      return;
    }

    const ret = f(sel, lines, line);
    if (ret === undefined) {
      return;
    }

    const range = document.createRange();
    range.setStart(ret.targetNode, ret.targetIndex);
    range.setEnd(ret.targetNode, ret.targetIndex);
    sel.removeAllRanges();
    sel.addRange(range);
    editableElement.focus();
  }

  // 行の移動
  static moveLine (
    currentLine: Node,
    anchorNode: Node,
    anchorIndex: number,
    destLine: Node
  ): { targetNode: Node; targetIndex: number } | undefined {
    // 現在行のIndexを取得
    const searched = ArrowKeySimulate.searchTextIndexFromTextNode(
      currentLine,
      anchorNode
    );
    let currentTextIndex = 0;
    if (searched[1]) {
      currentTextIndex = searched[0] + anchorIndex;
    }
    // 移動先行の文字数で制限する
    const nextLineLength = destLine.textContent?.length ?? 0;
    if (nextLineLength < currentTextIndex) {
      currentTextIndex = nextLineLength;
    }
    // 移動先行の中で指定Indexに該当するTextNodeを探す
    const sub = ArrowKeySimulate.searchTextNodeFromTextIndex(
      destLine,
      currentTextIndex
    );
    let targetNode = destLine;
    let targetIndex = 0;
    if (sub[0] !== undefined) {
      targetNode = sub[0];
      targetIndex = sub[1];
    }
    return {
      targetNode: targetNode,
      targetIndex: targetIndex,
    };
  }

  // 現在行のIndexを取得
  static searchTextIndexFromTextNode (p: Node, n: Node): [number, boolean] {
    // 自分がルートだったらそのまま返す
    if (p === n) {
      return [0, true];
    }
    let index = 0;
    const cs = Array.from(p.childNodes);
    for (let i = 0; i < cs.length; i++) {
      const c = cs[i];
      if (c === n) {
        return [index, true];
      } else if (c.nodeName == "#text") {
        index += c.textContent?.length ?? 0;
      } else {
        const sub = ArrowKeySimulate.searchTextIndexFromTextNode(c, n);
        index += sub[0];
        if (sub[1]) {
          return [index, true];
        }
      }
    }
    return [index, false];
  }
  // 移動先行の中で指定Indexに該当するTextNodeを探す
  static searchTextNodeFromTextIndex (
    p: Node,
    index: number
  ): [Node | undefined, number] {
    let stackIndex = 0;
    if (p.nodeName == "#text") {
      const textLength = p.textContent?.length ?? 0;
      if (index <= stackIndex + textLength) {
        return [p, index];
      }
      stackIndex += textLength;
      return [undefined, stackIndex];
    } else {
      const cs = Array.from(p.childNodes);
      for (let i = 0; i < cs.length; i++) {
        const c = cs[i];
        const sub = ArrowKeySimulate.searchTextNodeFromTextIndex(
          c,
          index - stackIndex
        );
        if (sub[0] !== undefined) {
          return [sub[0], sub[1]];
        } else {
          stackIndex += sub[1];
        }
      }
    }
    return [undefined, stackIndex];
  }
  // 指定された要素の直前のTextNodeを取得
  static findPrevTextNode (root: Node, n: Node) {
    // まずは同階層の直前要素を探す
    const prev = ArrowKeySimulate.findNeighbor(root, n, false);
    if (prev == null) {
      return null;
    }
    // TextNodeじゃなかったらその中の末尾にいるTextNodeを探索する
    const textNode = ArrowKeySimulate.findTextNode(prev, false);
    return textNode ?? prev;
  }
  // 指定された要素の直後のTextNodeを取得
  static findNextTextNode (root: Node, n: Node) {
    // まずは同階層の直後要素を探す
    const next = ArrowKeySimulate.findNeighbor(root, n, true);
    if (next == null) {
      return null;
    }
    // TextNodeじゃなかったらその中の先頭にいるTextNodeを探索する
    const textNode = ArrowKeySimulate.findTextNode(next, true);
    return textNode ?? next;
  }
  // 指定された要素の直前/直後の要素を取得
  static findNeighbor (root: Node, n: Node, isNext: boolean): Node | null {
    // まずは同階層の隣要素を探す
    let neighbor: Node | null;
    if (isNext) {
      neighbor = n.nextSibling;
    } else {
      neighbor = n.previousSibling;
    }

    if (neighbor == null) {
      // 存在しなかったら親階層の隣要素を探す
      if (n === root || n.parentNode == null) {
        return null;
      }
      return ArrowKeySimulate.findNeighbor(root, n.parentNode, isNext);
    }
    // 要素かテキスト以外は見つけない
    if (!(neighbor.nodeType == 1 || neighbor.nodeType == 3)) {
      return null;
    }
    return neighbor;
  }
  // 指定されたTextNodeを取得
  static findTextNode (n: Node, isFirst: boolean): Node | null {
    if (n.nodeName == "#text") {
      return n;
    }

    let textNode: Node | null;
    if (isFirst) {
      textNode = n.firstChild;
    } else {
      textNode = n.lastChild;
    }
    if (textNode == null) {
      return null;
    }
    if (textNode.nodeName != "#text") {
      const innerTextNode = ArrowKeySimulate.findTextNode(textNode, isFirst);
      if (innerTextNode) {
        return innerTextNode;
      }
      if (isFirst) {
        return ArrowKeySimulate.findNextTextNode(n, textNode);
      } else {
        return ArrowKeySimulate.findPrevTextNode(n, textNode);
      }
    }
    return textNode;
  }
  // 現在の行とカーソル位置(文字数)を取得
  static getLineFromInnerNode (lines: Node[], n: Node): Node | undefined {
    if (lines.includes(n)) {
      return n;
    }
    const parentNode = n.parentNode;
    if (parentNode == null) {
      return undefined;
    } else {
      return ArrowKeySimulate.getLineFromInnerNode(lines, parentNode);
    }
  }
}
